import { useMemo } from 'react';

import { RiDatabase2Line } from '@remixicon/react';

import { Text } from '@spotify-internal/encore-web';
import {
  type ToolGroupEntry,
  ToolLabel,
  type ToolRenderOptions,
  createToolExtension,
} from '@spotify/plugin-confidence-chat-react';

import { confidenceChatPlugin } from '../plugin';
import { formatLineCount } from './formatLineCount';
import {
  formatToolResultContent,
  getInputString,
  getToolInput,
  parseToolResultEnvelopeObject,
} from './parseToolResult';

import styles from './ExecuteSqlToolExtension.module.css';

const getSqlQuery = (input: unknown): string | undefined => {
  const values = getToolInput(input);
  return getInputString(values, 'sql_query') ?? getInputString(values, 'sql');
};

const formatSqlResultCount = (resultContent: string): string => {
  const result = parseToolResultEnvelopeObject(resultContent);
  const rows = result?.rows;
  if (!Array.isArray(rows)) {
    return formatLineCount(formatToolResultContent(resultContent));
  }
  const totalRows = result?.total_rows;
  const isTruncated = result?.truncated === true;
  const rowTotal =
    typeof totalRows === 'number' &&
    Number.isInteger(totalRows) &&
    totalRows >= 0
      ? totalRows
      : rows.length;
  const rowCount =
    isTruncated && rowTotal > rows.length
      ? `${rows.length} of ${rowTotal} rows`
      : `${rowTotal} ${rowTotal === 1 ? 'row' : 'rows'}`;
  const columns = result?.columns;
  if (!Array.isArray(columns)) return rowCount;
  const columnCount = `${columns.length} ${
    columns.length === 1 ? 'column' : 'columns'
  }`;
  return `${columnCount}, ${rowCount}`;
};

const SqlResultContent = ({ content }: { content: string }) => (
  <Text as="pre" variant="bodySmallMono" className={styles.sql}>
    {formatToolResultContent(content)}
  </Text>
);

type SqlScope = 'warehouse' | 'platform';

const sqlCopy: Record<
  SqlScope,
  {
    preparing: string;
    running: string;
    completed: string;
    failed: string;
    queryFallback: string;
  }
> = {
  warehouse: {
    preparing: 'Preparing SQL',
    running: 'Running SQL',
    completed: 'Ran SQL',
    failed: 'SQL failed',
    queryFallback: 'Warehouse query',
  },
  platform: {
    preparing: 'Preparing platform SQL',
    running: 'Running platform SQL',
    completed: 'Ran platform SQL',
    failed: 'Platform SQL failed',
    queryFallback: 'Platform query',
  },
};

const getSqlAction = (
  entry: ToolGroupEntry,
  { isStreaming }: ToolRenderOptions,
  scope: SqlScope,
): string => {
  const copy = sqlCopy[scope];
  if (isStreaming) return copy.preparing;
  if (!entry.result) return copy.running;
  return entry.result.success ? copy.completed : copy.failed;
};

export const ExecuteSqlToolLabel = ({
  entry,
  options,
  scope = 'warehouse',
}: {
  entry: ToolGroupEntry;
  options: ToolRenderOptions;
  scope?: SqlScope;
}) => {
  const sqlQuery = getSqlQuery(entry.input);
  const streamingQueryPreview = sqlQuery
    ? `...${sqlQuery.slice(-7)}`
    : undefined;
  const resultContent = entry.result?.content;
  const resultSummary = useMemo(
    () => (resultContent ? formatSqlResultCount(resultContent) : undefined),
    [resultContent],
  );
  let detail: string | undefined;
  if (entry.result?.success) {
    detail = resultSummary;
  } else if (options.isStreaming) {
    detail = streamingQueryPreview ?? 'Waiting for query';
  } else if (entry.result) {
    detail = sqlQuery ?? sqlCopy[scope].queryFallback;
  }
  return (
    <ToolLabel.Root>
      <ToolLabel.Primary>
        {getSqlAction(entry, options, scope)}
      </ToolLabel.Primary>
      {detail && (
        <ToolLabel.Secondary title={sqlQuery}>{detail}</ToolLabel.Secondary>
      )}
      <ToolLabel.Timer startedAt={entry.result ? undefined : entry.startedAt} />
      {(sqlQuery || entry.result) && (
        <ToolLabel.Details>
          <div className={styles.detailsBox}>
            {sqlQuery && (
              <div className={styles.section}>
                <Text as="div" variant="bodySmallBold">
                  SQL query
                </Text>
                <Text as="pre" variant="bodySmallMono" className={styles.sql}>
                  {sqlQuery}
                </Text>
              </div>
            )}
            {resultContent && (
              <div className={styles.section}>
                <Text as="div" variant="bodySmallBold">
                  Result
                </Text>
                <SqlResultContent content={resultContent} />
              </div>
            )}
          </div>
        </ToolLabel.Details>
      )}
    </ToolLabel.Root>
  );
};

export const ExecuteSqlToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'data-explorer-execute-sql',
    predicate: tool => tool === 'mcp__data_explorer__execute_sql',
    icon: RiDatabase2Line,
    display: 'grouped',
    collapseConsecutiveCalls: false,
    render: () => null,
    renderLabel: (entry, options) => (
      <ExecuteSqlToolLabel entry={entry} options={options} />
    ),
  }),
);
